import type {
  SettleShopifyApprovalRequestBodyType,
  SettleShopifyApprovalResponseBodyType,
} from "@intelligems/ig-types/functions/billing-workflow/shopify-approval/shopify-approval";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";

import { api } from "../../api";
import type { ApiRequest } from "../../types/api";

export type SettleShopifyApprovalReturnVariables = ApiRequest<
  SettleShopifyApprovalRequestBodyType,
  undefined,
  undefined
>;

type SettleShopifyApprovalReturnOptions = Omit<
  UseMutationOptions<
    SettleShopifyApprovalResponseBodyType,
    Error,
    SettleShopifyApprovalReturnVariables
  >,
  "mutationFn" | "mutationKey" | "onError"
>;

export const settleShopifyApprovalReturn = async (
  variables: SettleShopifyApprovalReturnVariables
): Promise<SettleShopifyApprovalResponseBodyType> => {
  const resp = await api.post<SettleShopifyApprovalResponseBodyType>(
    "/v3/billing/shopify-approval/settle",
    variables.body
  );
  return resp.data;
};

export const useSettleShopifyApprovalReturn = (
  options?: SettleShopifyApprovalReturnOptions
) =>
  useMutation<
    SettleShopifyApprovalResponseBodyType,
    Error,
    SettleShopifyApprovalReturnVariables
  >({
    ...options,
    mutationKey: ["settle-shopify-approval-return"],
    mutationFn: settleShopifyApprovalReturn,
    // Settling is a shortcut past the poll, not something the merchant asked
    // for: an approval the endpoint cannot match yet (404) or any other
    // failure falls back to the poll quietly instead of raising a toast.
    onError: (error) => {
      (error as { suppressToast?: boolean }).suppressToast = true;
    },
  });
